import { useTranslation } from "@sagot/i18n";
import { Button, Checkbox, DialogClose, DialogContent, Icon, Input, Label, Textarea, toast } from "@sagot/ui";
import { useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { zodResolver } from "../../../lib/zod-resolver";
import { useFormatters } from "../shared/format";
import { useAgentList, useAudienceList } from "../shared/use-engage-data";
import { AudienceSection, ChannelSection } from "./composer-audience";
import { composerSchema, emptyValues, sendAtOf, toSaveRequest, valuesFrom, type ComposerValues, type SaveIntent } from "./composer-form";
import { ComposerPreview } from "./composer-preview";
import { TeachSection, WhenSection } from "./composer-timing";
import { BODY_MAX, PUSH_PREVIEW_CHARS, TITLE_MAX, type Announcement } from "./model";
import { useReachEstimate, useSaveAnnouncement } from "./use-announcements";

interface ComposerProps {
  editing: Announcement | null;
  canSend: boolean;
  segmentOptions: readonly string[];
  onDone: () => void;
}

const K = "engage.announcements.composer";

function successToast(intent: SaveIntent, values: ComposerValues): string {
  if (intent === "draft") return "engage.announcements.toasts.draftSaved";
  if (values.when === "later") return values.teach ? "engage.announcements.toasts.scheduledTeach" : "engage.announcements.toasts.scheduled";
  return values.teach ? "engage.announcements.toasts.sentTeach" : "engage.announcements.toasts.sent";
}

/** Compose / edit dialog (mockup `compose-modal`): form left, live previews right. */
export function AnnouncementComposer({ editing, canSend, segmentOptions, onDone }: ComposerProps) {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const audiences = useAudienceList();
  const agents = useAgentList();
  const save = useSaveAnnouncement();
  const [intent, setIntent] = useState<SaveIntent>("send");
  const [scheduleError, setScheduleError] = useState<string | undefined>();
  const form = useForm<ComposerValues>({
    resolver: zodResolver(composerSchema),
    defaultValues: editing ? valuesFrom(editing, fmt.timeZone) : emptyValues(fmt.timeZone),
  });
  const { register, control, formState } = form;
  const values = useWatch({ control }) as ComposerValues;
  const reach = useReachEstimate({ audiences: values.audiences, segments: values.segments, channels: values.channels }, true);
  const fieldError = (key: keyof ComposerValues) => {
    const message = formState.errors[key]?.message;
    return message ? t(`${K}.${message}`) : undefined;
  };

  const submit = (chosen: SaveIntent) =>
    form.handleSubmit(async (data) => {
      const sendAt = sendAtOf(data, fmt.timeZone);
      if (chosen === "send" && sendAt === undefined) {
        setScheduleError(t(`${K}.scheduleInvalid`));
        return;
      }
      setScheduleError(undefined);
      try {
        await save.mutateAsync(toSaveRequest(data, { intent: chosen, sendAt: sendAt ?? null, timeZone: fmt.timeZone, editing }));
        toast.success(t(successToast(chosen, data)));
        onDone();
      } catch {
        toast.error(t("engage.announcements.toasts.failed"));
      }
    });

  const sendLabel = values.when === "later" ? t(`${K}.scheduleSubmit`) : t(`${K}.send`);
  const busy = save.isPending;

  return (
    <DialogContent
      title={editing ? t(`${K}.editTitle`) : t(`${K}.newTitle`)}
      closeLabel={t("common.close")}
      className="flex max-h-[92vh] max-w-5xl flex-col [&>div:nth-child(2)]:min-h-0 [&>div:nth-child(2)]:flex-1 [&>div:nth-child(2)]:overflow-y-auto [&>div:nth-child(2)]:p-0"
      footer={
        <div className="flex w-full flex-wrap items-center justify-between gap-2">
          <Button type="button" variant="ghost" loading={busy && intent === "draft"} disabled={busy} onClick={() => { setIntent("draft"); void submit("draft")(); }}>
            {t(`${K}.saveDraft`)}
          </Button>
          <div className="flex gap-2">
            <DialogClose asChild>
              <Button type="button" variant="secondary">
                {t("common.cancel")}
              </Button>
            </DialogClose>
            {canSend ? (
              <Button type="submit" form="ann-compose" loading={busy && intent === "send"} disabled={busy}>
                <Icon name={values.when === "later" ? "calendar" : "send"} />
                {sendLabel}
              </Button>
            ) : null}
          </div>
        </div>
      }
    >
      <form
        id="ann-compose"
        noValidate
        className="grid grid-cols-1 lg:grid-cols-[1fr_20rem]"
        onSubmit={(e) => {
          setIntent("send");
          void submit("send")(e);
        }}
      >
        <div className="space-y-5 p-5">
          {!canSend ? <p className="rounded-md bg-sunken px-3 py-2 text-xs text-fg-muted">{t(`${K}.noSendPermission`)}</p> : null}
          <div>
            <Label htmlFor="ann-title">{t(`${K}.titleLabel`)}</Label>
            <Input id="ann-title" maxLength={TITLE_MAX} placeholder={t(`${K}.titlePlaceholder`)} invalid={Boolean(formState.errors.title)} aria-describedby={formState.errors.title ? "ann-title-error" : undefined} {...register("title")} />
            {fieldError("title") ? <p id="ann-title-error" className="mt-1 text-xs font-semibold text-danger">{fieldError("title")}</p> : null}
          </div>
          <div>
            <div className="flex items-center justify-between gap-2">
              <Label htmlFor="ann-body">{t(`${K}.messageLabel`)}</Label>
              <span className="text-[11px] tabular-nums text-fg-subtle">{t(`${K}.messageCounter`, { count: values.body.length, max: BODY_MAX, push: PUSH_PREVIEW_CHARS })}</span>
            </div>
            <Textarea id="ann-body" rows={5} maxLength={BODY_MAX} placeholder={t(`${K}.messagePlaceholder`)} invalid={Boolean(formState.errors.body)} aria-describedby={formState.errors.body ? "ann-body-error" : undefined} {...register("body")} />
            {fieldError("body") ? <p id="ann-body-error" className="mt-1 text-xs font-semibold text-danger">{fieldError("body")}</p> : null}
            <div className="mt-2">
              <Controller control={control} name="urgent" render={({ field }) => <Checkbox checked={field.value} onCheckedChange={(c) => { field.onChange(c === true); }} label={t(`${K}.urgent`)} />} />
            </div>
          </div>
          <AudienceSection control={control} audiences={audiences.data ?? []} segmentOptions={segmentOptions} error={fieldError("audiences")} />
          <ChannelSection control={control} />
          {canSend ? <WhenSection control={control} register={register} when={values.when} timeZone={fmt.timeZone} scheduleError={scheduleError} /> : null}
          <TeachSection control={control} register={register} teach={values.teach} agents={agents.data ?? []} />
        </div>
        <ComposerPreview title={values.title} body={values.body} reach={reach.data} reachPending={reach.isFetching} />
      </form>
    </DialogContent>
  );
}
